import { normalize, utf8Length } from './posix.js';
/**
 * Path and content rules for the in-browser editor's file API.
 *
 * Everything the editor touches is named by the learner's browser, so this
 * module is the trust boundary: it turns an arbitrary string into a path that
 * is provably inside the mission working directory, or refuses it.
 *
 * It lives here rather than beside the HTTP routes so the rules can be unit
 * tested without starting a server (see server/test/files.test.ts), and so a
 * client that runs the engine in the browser applies exactly the same ones.
 */
/** Largest file the editor will read or write, in bytes. */
export const MAX_EDIT_BYTES = 512 * 1024;
/** Entries returned by one listing. */
export const MAX_LIST_ENTRIES = 500;
function httpError(status, message) {
    return Object.assign(new Error(message), { status });
}
/** Trim a mission cwd ("storefront", "", "./x/") down to a clean relative prefix. */
function missionPrefix(cwd) {
    const clean = normalize(cwd.replace(/\\/g, '/')).replace(/^\.?\/*/, '').replace(/\/+$/, '');
    return clean === '.' ? '' : clean;
}
/**
 * Validate a client-supplied path and return it in normalised POSIX form.
 *
 * `""` and `"."` both mean "the directory itself" and come back as `""`.
 * Everything else must be a plain relative path: no leading slash, no drive
 * letter, no backslash (the local driver runs on Windows, where `a\..\..\b`
 * would escape), no `.` or `..` segment, no NUL, no `.git`, and already in
 * normalised form so there is nothing left for a later normalisation to
 * change.
 */
export function validateRelPath(input) {
    if (typeof input !== 'string')
        throw httpError(400, 'path must be a string');
    if (input === '' || input === '.')
        return '';
    if (input.includes('\0'))
        throw httpError(400, 'path must not contain a NUL byte');
    if (input.length > 1024)
        throw httpError(400, 'path is too long');
    if (input.includes('\\'))
        throw httpError(400, 'path must use forward slashes');
    if (input.startsWith('/') || /^[A-Za-z]:/.test(input))
        throw httpError(400, 'path must be relative, not absolute');
    if (input.endsWith('/'))
        throw httpError(400, 'path must not end with a slash');
    const segments = input.split('/');
    if (segments.some((s) => s === '' || s === '.' || s === '..')) {
        throw httpError(400, `path "${input}" is not a normalised relative path`);
    }
    if (normalize(input) !== input) {
        throw httpError(400, `path "${input}" is not a normalised relative path`);
    }
    if (segments.includes('.git')) {
        // The grader reads this repository; a corrupted object store leaves the
        // learner with a level they cannot recover.
        throw httpError(403, 'the .git directory is not editable');
    }
    return input;
}
/**
 * Resolve a client-supplied path against the mission working directory.
 *
 * The path is normalised *first* and the result is then checked to still sit
 * under the mission prefix, so the guarantee does not rest on the string
 * checks above alone.
 */
export function resolveMissionPath(missionCwd, input) {
    const rel = validateRelPath(input);
    const prefix = missionPrefix(missionCwd);
    const full = normalize(prefix ? (rel ? `${prefix}/${rel}` : prefix) : rel);
    if (prefix && full !== prefix && !full.startsWith(`${prefix}/`)) {
        throw httpError(400, `path "${rel}" escapes the mission working directory`);
    }
    if (full.split('/').includes('.git'))
        throw httpError(403, 'the .git directory is not editable');
    return { rel, full };
}
/** A file the editor must not open: too large, binary, or not valid UTF-8. */
export function isEditableText(content) {
    if (utf8Length(content) > MAX_EDIT_BYTES)
        return false;
    // Both drivers decode as UTF-8, so invalid bytes arrive as U+FFFD. Treat
    // those (and embedded NULs) as "binary" rather than handing the editor
    // content it would silently corrupt on save.
    return !content.includes('\0') && !content.includes('\uFFFD');
}
//# sourceMappingURL=files.js.map